import { useAuth0 } from '@auth0/auth0-react';
import {
  ConfirmUserCompanyResponse,
  GetOnboardingStatusResponse,
} from '@dreamai/types';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { PusherProvider } from './contexts/PusherContext';
import { Providers as DependencyProviders } from './providers';
import './utils/testDependencyUpdate';

import api from './api';
import GoLogin from './components/GoLogin';
import AppLoading from './components/misc/AppLoading';

import { CommentsProvider } from './contexts/CommentsContext';
import { FormCaptureProvider } from './contexts/FormCaptureContext';
import { LinkedThreadsProvider } from './contexts/LinkedThreadsContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { NewThreadProvider } from './contexts/NewThreadContext';
import { ThreadLoadingProvider } from './contexts/ThreadLoadingContext';

import BookingPage from './components/BookingPage';
import ConversationalUI from './components/ConversationalUI';
import SharedProjectAppPage from './components/SharedProjectAppPage';
import SharedArtifactPage from './components/SharedArtifactPage';
import SharedViewPage from './components/SharedViewPage';
import WebRTCVoiceChat from './components/WebRTCVoiceChat';
import SharedTodayUpdatePage from './components/chat/SharedTodayUpdatePage';
import CreditsPusherListener from './components/common/CreditsPusherListener';
import TFAPusherListener from './components/common/TFAPusherListener';
import OnboardingFlow from './components/onboarding/OnboardingFlow';
import { CompanyProvider, useCompany } from './contexts/CompanyContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import './index.scss';

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);
  const {
    setMemoryEnabled,
    setHasSubscription,
    setAiTimeMinutes,
    setResetsAt,
    setHasPersonalAssistant,
  } = useCompany();

  const navigate = useNavigate();

  useEffect(() => {
    const checkCompanyStatus = async (): Promise<void> => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          const response = await api.get<ConfirmUserCompanyResponse>(
            '/confirm-user-company',
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          setMemoryEnabled(response.data.memory_enabled || false);
          setHasSubscription(response.data.has_subscription || false);
          setAiTimeMinutes(response.data.ai_time_minutes ?? 0);
          setResetsAt(response.data.resets_at ?? null);
          setHasCompany(response.data.has_company);
          setHasPersonalAssistant(
            response.data.has_personal_assistant || false,
          );

          // Check onboarding status — redirect if incomplete
          try {
            const onboardingRes = await api.get<GetOnboardingStatusResponse>(
              '/onboarding/status',
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (
              onboardingRes.data.success &&
              !onboardingRes.data.status.onboardingCompleted
            ) {
              // Only redirect if not already on /onboarding (preserves query
              // params from external redirects like Airwallex checkout)
              if (!window.location.pathname.startsWith('/onboarding')) {
                navigate('/onboarding', { replace: true });
              }
            }
          } catch (onboardingErr) {
            // If status endpoint fails (e.g. no row yet), skip redirect
            console.warn('Could not check onboarding status:', onboardingErr);
          }
        } catch (error) {
          console.error('Error checking company status:', error);
        }
      }
    };

    checkCompanyStatus();
  }, [isAuthenticated, navigate, getAccessTokenSilently]);

  if (isLoading || (!isLoading && isAuthenticated && hasCompany === null)) {
    return <AppLoading />;
  }

  // If the page is a shared update, for now just load only that route, no difference between
  // authenticated or not authenticated app
  if (window.location.pathname.startsWith('/updates/shared')) {
    return (
      <Routes>
        <Route
          path='/updates/shared/:updateId/:imageId'
          element={<SharedTodayUpdatePage />}
        />
      </Routes>
    );
  }

  // Public routes — render regardless of auth state so logged-in users can
  // view shared pages / booking pages without being swallowed by the app shell.
  if (
    !isLoading &&
    (window.location.pathname.startsWith('/booking/') ||
      window.location.pathname.startsWith('/shared/') ||
      window.location.pathname.startsWith('/artifact/') ||
      window.location.pathname.startsWith('/shared-view/'))
  ) {
    return (
      <Routes>
        <Route path='/booking/:key' element={<BookingPage />} />
        <Route path='/shared/:shareToken' element={<SharedProjectAppPage />} />
        <Route path='/artifact/:shareToken' element={<SharedArtifactPage />} />
        <Route path='/shared-view/:shareToken' element={<SharedViewPage />} />
        <Route path='*' element={<GoLogin />} />
        <Route path='/voice-chat' element={<WebRTCVoiceChat />} />
      </Routes>
    );
  }

  if (!isLoading && !isAuthenticated) {
    return <GoLogin />;
  }

  if (isAuthenticated) {
    // Onboarding flow — rendered outside normal layout
    if (window.location.pathname.startsWith('/onboarding')) {
      return (
        <PusherProvider>
          <Routes>
            <Route path='/onboarding' element={<OnboardingFlow />} />
          </Routes>
        </PusherProvider>
      );
    }

    return (
      <PusherProvider>
        <CreditsPusherListener />
        <TFAPusherListener />
        <DependencyProviders>
          <MessagingProvider>
            <NewThreadProvider>
              <ThreadLoadingProvider>
                <FormCaptureProvider>
                  <CommentsProvider>
                    <LinkedThreadsProvider>
                      <ConversationalUI />
                    </LinkedThreadsProvider>
                  </CommentsProvider>
                </FormCaptureProvider>
              </ThreadLoadingProvider>
            </NewThreadProvider>
          </MessagingProvider>
        </DependencyProviders>
      </PusherProvider>
    );
  }

  /**
   * Fallback to loading forever, this should never logically happen, if it does, there is a bug upwards
   */
  return <AppLoading />;
};

const App: React.FC = () => {
  return (
    <CompanyProvider>
      <FeatureFlagsProvider>
        <AppContent />
      </FeatureFlagsProvider>
    </CompanyProvider>
  );
};

export default App;
